import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Alert, Modal, useAlert } from '../../components';
import { FileDrop, useReplaceAsset, useReplaceAssetInChunks } from '../event-details';

interface Props {
  open: boolean;
  onClose: () => void;
  assetId: number;
  clientId: string;
  /** Runs once the new file is in place, to refresh the event and its player. */
  onReplaced: () => void;
}

/** Above this size the file goes up in parts (S3 multipart) instead of one PUT. */
const MULTIPART_THRESHOLD = 5 * 1024 * 1024 * 1024;

/*
 * The archive URL is not servable the instant FinishAssetReplace resolves, so we
 * hold the modal open briefly before refreshing the player — the same wait the
 * old Command Center used.
 */
const PROCESSING_MS = 5000;

const Drop = styled.div`
  .zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 38px 20px;
    border: 1px dashed rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    background: rgba(8, 14, 30, 0.5);
    color: rgba(176, 186, 209, 0.7);
    font-size: 13px;
    text-align: center;
    cursor: pointer;
    transition: all var(--transition);
  }

  .zone:hover {
    border-color: rgba(120, 130, 255, 0.4);
    background: rgba(17, 27, 51, 0.6);
  }

  i {
    font-size: 26px;
    color: rgba(159, 176, 255, 0.9);
  }

  .name {
    color: var(--white);
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 28px 20px;
  text-align: center;

  .pct {
    color: var(--white);
    font-weight: 700;
    font-size: 26px;
  }

  .label {
    color: rgba(176, 186, 209, 0.7);
    font-size: 12px;
  }

  .track {
    height: 6px;
    border-radius: 99px;
    background: rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }

  .bar {
    height: 100%;
    border-radius: 99px;
    background: rgb(78, 95, 253);
    transition: width 0.2s linear;
  }

  .file {
    color: rgba(176, 186, 209, 0.55);
    font-size: 11px;
    overflow-wrap: anywhere;
  }
`;

const Hint = styled.div`
  color: rgba(176, 186, 209, 0.55);
  font-size: 11px;
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  button {
    border: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 12px;
    padding: 9px 15px;
    cursor: pointer;
    transition: opacity 0.15s;
  }

  button:hover,
  button:focus {
    outline: none;
    opacity: 0.92;
  }

  button:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .cancel {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: rgba(213, 221, 238, 0.92);
  }
`;

interface FileData {
  file: File;
  duration: number;
}

/*
 * Replaces the recording of a past event with a file the user picks: asks the
 * backend for a signed URL, uploads straight to it, then hands back the final
 * URL so the asset points at the new video. Destructive — it overwrites the
 * recording clients see, and the previous file is not kept anywhere.
 */
export const ReplaceAssetModal = ({
  open,
  onClose,
  assetId,
  clientId,
  onReplaced
}: Props): ReactElement => {
  const [fileData, setFileData] = useState<FileData>();
  const [processing, setProcessing] = useState(false);
  const alert = useAlert();

  const [replaceAsset, single] = useReplaceAsset();
  const [replaceAssetInParts, multipart] = useReplaceAssetInChunks();

  const uploading = single.loading || multipart.loading;
  const progress = multipart.loading ? multipart.progress : single.progress;
  const busy = uploading || processing;

  const { error: singleError } = single;
  const { error: multipartError } = multipart;

  useEffect(() => {
    const failure = singleError ?? multipartError;

    if (failure) {
      alert.show(failure.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alert.show is stable
  }, [singleError, multipartError]);

  // Duration comes from the file itself; the backend stores it with the asset.
  const onDrop = useCallback((files: File[]): void => {
    if (files.length === 0) {
      return;
    }

    const [file] = files;

    const video = document.createElement('video');

    video.preload = 'metadata';

    video.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(video.src);
      setFileData({ file, duration: video.duration });
    });

    // Unreadable metadata still uploads; the backend just gets no duration.
    video.addEventListener('error', () => {
      URL.revokeObjectURL(video.src);
      setFileData({ file, duration: 0 });
    });

    video.src = URL.createObjectURL(file);
  }, []);

  const close = useCallback((): void => {
    // Closing mid-upload would orphan it: the request keeps going with no UI.
    if (busy) {
      return;
    }

    setFileData(undefined);
    onClose();
  }, [busy, onClose]);

  const cancel = useCallback((): void => {
    single.abort?.();
    multipart.abort?.();
    setFileData(undefined);
    setProcessing(false);
    onClose();
  }, [single, multipart, onClose]);

  const upload = useCallback((): void => {
    if (!fileData || busy) {
      return;
    }

    const args = {
      assetId,
      clientId: Number(clientId),
      file: fileData.file,
      duration: fileData.duration
    };

    void (async (): Promise<void> => {
      try {
        await (fileData.file.size > MULTIPART_THRESHOLD
          ? replaceAssetInParts(args)
          : replaceAsset(args));

        setProcessing(true);

        await new Promise<void>((resolve) => {
          setTimeout(resolve, PROCESSING_MS);
        });

        onReplaced();
        setProcessing(false);
        setFileData(undefined);
        onClose();
      } catch {
        // The hooks expose the failure; the effect above turns it into an alert.
        setProcessing(false);
      }
    })();
  }, [fileData, busy, assetId, clientId, replaceAsset, replaceAssetInParts, onReplaced, onClose]);

  const footer = (
    <Actions>
      <button type='button' className='cancel' onClick={busy ? cancel : close}>
        Cancel
      </button>
      <button
        type='button'
        className='cc-primary'
        disabled={fileData === undefined || busy}
        onClick={upload}
      >
        Replace asset
      </button>
    </Actions>
  );

  return (
    <Modal open={open} onClose={close} title='Replace event asset' footer={footer} width={520}>
      {alert.message !== undefined && <Alert message={alert.message} onDismiss={alert.clear} />}

      {busy ? (
        <Progress>
          <div className='pct'>{progress.toFixed(0)}%</div>
          <div className='track'>
            <div className='bar' style={{ width: `${progress}%` }} />
          </div>
          <div className='label'>{processing ? 'Processing video…' : 'Uploading video…'}</div>
          <div className='file'>{fileData?.file.name}</div>
        </Progress>
      ) : (
        <>
          <Drop>
            <FileDrop className='zone' onDrop={onDrop}>
              <i className='fal fa-cloud-upload' />
              {fileData ? (
                <>
                  <span className='name'>{fileData.file.name}</span>
                  <span>Choose a different file</span>
                </>
              ) : (
                <span>Drop your file here, or click to pick one</span>
              )}
            </FileDrop>
          </Drop>
          <Hint>MP4 only. This overwrites the recording clients see.</Hint>
        </>
      )}
    </Modal>
  );
};
